// src/pages/TableReservationsPage.tsx
import { useState, useEffect } from 'react';
import { FaUsers, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchTableAvailabilityRequest } from '../redux/slices/tableAvailabilitySlice';
import { makeReservationRequest, resetReservationState } from '../redux/slices/makeReservationSlice';
import { RootState } from '../redux/store';
import { getRestaurantId } from '../api/tokenUtils';
import { BackButton, ReservationSuccessPopup, TimeSlotSelector } from '../components';
import {
  checkIfRestaurantClosed,
  formatStartTimesToEST,
  getStartAndEndTime
} from '../utils/reservationUtils';



export default function TableReservationsPage() {
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('');
  const [name, setName] = useState('');
  const [partySize, setPartySize] = useState(1);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [specialRequest, setSpecialRequest] = useState('');

  const { time_slots, loading: tableLoading, noTablesAvailable } = useAppSelector((state: RootState) => state.tableAvailability);
  const { loading: reservationLoading, success: reservationSuccess, reservationData } = useAppSelector((state: RootState) => state.makeReservation);
  const siteContent = useAppSelector((state: RootState) => state.siteContent);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRestaurantClosed, setIsRestaurantClosed] = useState(false);

  const selectedRestaurant = useAppSelector((state: RootState) => state.restaurant.selectedRestaurant);

  // Check if today is closed on mount
  useEffect(() => {
    const isClosed = checkIfRestaurantClosed(selectedDate, siteContent);
    setIsRestaurantClosed(isClosed);
  }, [selectedDate, siteContent]); // Re-check when siteContent loads or date changes

  // Fetch availability when date changes
  useEffect(() => {
    if (selectedDate) {
      dispatch(fetchTableAvailabilityRequest({ date: selectedDate }));
    }
  }, [selectedDate, dispatch]);

  // Reset success state when component mounts
  useEffect(() => {
    setShowSuccessPopup(false);
    dispatch(resetReservationState());
  }, [dispatch]);

  // Show success popup when reservation succeeds
  useEffect(() => {
    if (reservationSuccess && reservationData) {
      setShowSuccessPopup(true);
      window.scroll(0, 0);
    }
  }, [reservationSuccess, reservationData]);

  // Format time slots to EST - trust Redux saga filtering
  useEffect(() => {

    // Step 1: Check if restaurant is closed first
    if (isRestaurantClosed) {
      setTimeSlots([]);
      return;
    }

    // Step 2: Use pre-filtered time slots from Redux (already filtered by available tables in saga)
    // No fallback - trust the API/saga filtering
    if (time_slots && time_slots.length > 0) {
      setTimeSlots(formatStartTimesToEST(time_slots, selectedDate));
    } else {
      // API returned no available slots
      setTimeSlots([]);
    }
  }, [time_slots, selectedDate, isRestaurantClosed]);

  // Handle date change with immediate closed check
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSelectedTime(''); // Clear selected time when date changes
    const isClosed = checkIfRestaurantClosed(newDate, siteContent);
    setIsRestaurantClosed(isClosed); // Immediately update closed state
  };

  const handleBooking = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    // Validate required data
    const restaurant_id = getRestaurantId();
    const restaurant_parent_id = selectedRestaurant?.restaurant_parent_id || '';

    if (!restaurant_id) {
      alert('Session expired. Please refresh the page and try again.');
      return;
    }

    const date = getStartAndEndTime(selectedDate, selectedTime);

    const reservationPayload = {
      restaurant_id: restaurant_id || '',
      restaurant_parent_id,
      party_size: partySize,
      customer_name: name,
      phone: phone,
      email: email,
      reservation_start_time: date.startTime,
      reservation_end_time: date.endTime,
      notes: specialRequest,
    };

    dispatch(makeReservationRequest(reservationPayload));
  };

  return (
    <>
      <div className="w-full bg-white min-h-screen pt-32 md:pt-36 lg:pt-40" style={{ fontFamily: 'serif' }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto p-4 sm:p-8"
        >
          <BackButton text="Back" />
          <div className={!tableLoading && !isRestaurantClosed && noTablesAvailable ? "" : "grid grid-cols-1 lg:grid-cols-2 gap-12"}>
            {/* Left side - Reservation Form or No Tables Message */}
            {!tableLoading && !isRestaurantClosed && noTablesAvailable ? (
              // Centered "No tables available" message (full width)
              <div className="max-w-2xl mx-auto">
                <div className="bg-white dark:bg-[#4A5568] p-12 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600 text-center">
                  <div className="text-6xl mb-4">🚫</div>
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                    No Tables Available
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                    Unfortunately, there are no tables available at the moment.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 font-semibold mb-4 text-lg">
                    Please contact the restaurant:
                  </p>
                  {selectedRestaurant?.phone && (
                    <a
                      href={`tel:${selectedRestaurant.phone}`}
                      className="inline-block px-8 py-4 bg-[#FF6347] text-white font-semibold text-lg rounded-lg hover:opacity-90 transition-opacity"
                    >
                      📞 {selectedRestaurant.phone}
                    </a>
                  )}
                </div>
              </div>
            ) : (
              // Show form
              <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600 relative">
                <h1 className="font-bold text-4xl mb-6 text-gray-800 dark:text-gray-200">Make a Reservation</h1>

                <form onSubmit={handleBooking} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  {/* Party Size and Date */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="partySize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                        <FaUsers className="inline-block mr-1 h-4 w-4" /> Party Size
                      </label>
                      <input
                        type="number"
                        id="partySize"
                        value={partySize}
                        onChange={(e) => setPartySize(Number(e.target.value))}
                        min="1"
                        max="50"
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                        placeholder="Enter party size"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        id="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                        required
                      />
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div>
                    <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select a Time</p>
                    <TimeSlotSelector
                      loading={tableLoading}
                      isRestaurantClosed={isRestaurantClosed}
                      timeSlots={timeSlots}
                      selectedTime={selectedTime}
                      onTimeSelect={(time) => setSelectedTime(selectedTime === time ? '' : time)}
                      selectedDate={selectedDate}
                      siteContent={siteContent}
                    />
                  </div>

                  {/* Phone and Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                        placeholder="Your phone number"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                        placeholder="Your email"
                        required
                      />
                    </div>
                  </div>

                  {/* Special Request */}
                  <div>
                    <label htmlFor="specialRequest" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Special Request <span className="text-gray-500 text-sm">(Optional)</span>
                    </label>
                    <textarea
                      id="specialRequest"
                      value={specialRequest}
                      onChange={(e) => setSpecialRequest(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                      placeholder="Any special requests or notes"
                      rows={3}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={
                      reservationLoading ||
                      name.trim() === '' ||
                      partySize <= 0 ||
                      !selectedDate ||
                      selectedTime === '' ||
                      phone.trim() === '' ||
                      email.trim() === '' ||
                      isRestaurantClosed ||
                      timeSlots.length === 0
                    }
                    className={`w-full font-bold text-xl py-4 rounded-lg transition-opacity duration-200 shadow-lg ${reservationLoading ||
                        name.trim() === '' ||
                        partySize <= 0 ||
                        !selectedDate ||
                        selectedTime === '' ||
                        phone.trim() === '' ||
                        email.trim() === '' ||
                        isRestaurantClosed ||
                        (!tableLoading && timeSlots.length === 0)
                        ? 'bg-[#FF6347]/60 cursor-not-allowed'
                        : 'bg-[#FF6347] hover:opacity-90'
                      } text-white flex items-center justify-center`}
                  >
                    {reservationLoading ? (
                      <>
                        <FaSpinner className="animate-spin mr-2 h-5 w-5" />
                        Processing...
                      </>
                    ) : (
                      'Book My Table!'
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Right side - Restaurant Information (Only show when form is visible) */}
            {!(!tableLoading && !isRestaurantClosed && noTablesAvailable) && (
              <div className="space-y-8">
                <h2 className="font-bold text-4xl text-gray-800 dark:text-gray-200">Restaurant Information</h2>

                <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600">
                  <h3 className="font-bold text-2xl mb-6 text-gray-800 dark:text-gray-200">Operating Hours</h3>
                  <div className="space-y-4">
                    {(() => {
                      const hours = (siteContent?.data as any)?.reservation?.info?.hours;
                      if (!hours) {
                        return (
                          <div className="p-4 bg-gray-50 dark:bg-[#2D3748] rounded-lg text-center">
                            <p className="text-gray-500 dark:text-gray-400">Hours not available</p>
                          </div>
                        );
                      }

                      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                      const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

                      // Helper function to format time from 24hr to 12hr
                      const formatTime = (time24: string) => {
                        const [hours, minutes] = time24.split(':').map(Number);
                        const period = hours >= 12 ? 'PM' : 'AM';
                        const hours12 = hours % 12 || 12;
                        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
                      };

                      return dayNames.map((dayKey, index) => {
                        const dayHours = hours[dayKey];
                        const dayLabel = dayLabels[index];

                        // Determine hours text based on data availability
                        let hoursText = '-'; // Default to dash if no data
                        let isClosed = false;

                        if (dayHours) {
                          if (dayHours.is_closed === true) {
                            // Explicitly closed
                            hoursText = 'Closed';
                            isClosed = true;
                          } else {
                            // Has hours data - build the hours string
                            const segments = [];
                            if (dayHours.firstHalf?.start && dayHours.firstHalf?.end) {
                              segments.push(`${formatTime(dayHours.firstHalf.start)} - ${formatTime(dayHours.firstHalf.end)}`);
                            }
                            if (dayHours.secondHalf?.start && dayHours.secondHalf?.end) {
                              segments.push(`${formatTime(dayHours.secondHalf.start)} - ${formatTime(dayHours.secondHalf.end)}`);
                            }
                            if (segments.length > 0) {
                              hoursText = segments.join(', ');
                            }
                          }
                        }

                        return (
                          <div key={dayKey} className="flex justify-between items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-[#4A5568]">
                            <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">{dayLabel}</span>
                            <span className={`${isClosed ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                              {hoursText}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {selectedRestaurant && (
                  <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600 relative">
                    <h3 className="font-bold text-2xl mb-4 text-gray-800 dark:text-gray-200">Find Us</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">{selectedRestaurant.address}</p>
                    {selectedRestaurant.phone && (
                      <p className="text-gray-600 dark:text-gray-300">{selectedRestaurant.phone}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Success Popup Modal */}
          <ReservationSuccessPopup
            isOpen={showSuccessPopup}
            message={(() => {
              if (!reservationData) return '';
              try {
                return JSON.parse(reservationData)?.message || 'Reservation processed';
              } catch {
                return 'Reservation processed';
              }
            })()}
            isSuccess={(() => {
              if (!reservationData) return false;
              try {
                return JSON.parse(reservationData)?.message?.toLowerCase()?.includes('table booked') || false;
              } catch {
                return false;
              }
            })()}
            onClose={() => {
              setShowSuccessPopup(false);
              dispatch(resetReservationState());
            }}
            onMakeAnother={() => {
              setShowSuccessPopup(false);
              dispatch(resetReservationState());
              setName('');
              setPartySize(1);
              setPhone('');
              setEmail('');
              setSpecialRequest('');
              setSelectedTime('');
              setSelectedDate(new Date().toISOString().split('T')[0]);
            }}
          />
        </motion.div>
      </div>
    </>
  );
}
